/**
 * Knowledge Bases Page - 지식 파이프라인
 * 
 * 멀티 소스 지원: 파일 업로드 + 웹 크롤링 + DB를 하나의 지식베이스에 합칠 수 있음
 * 
 * mode:
 * - 'admin' (기본): 전체 기능 (관리자용)
 * - 'user': 개인 파일 업로드만 가능 (사용자 포탈용)
 * 
 * URL 쿼리 파라미터:
 * - source=file: 파일 소스만 포함된 KB
 * - source=db: DB 소스만 포함된 KB
 * - source=web: 웹 소스만 포함된 KB
 * - (없음): 복합 데이터 소스 (2개 이상 소스)
 */

import { isAdminRole } from '../utils/role';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Topbar } from '../components/layout';
import { Button, Badge, Select, SearchInput, Pagination, useToast, Modal } from '../components/ui';
import type { BadgeVariant, BadgeColor } from '../components/ui';
import { 
  knowledgeBaseApi, 
  KnowledgeBase, 
  SearchResult,
  workflowApi,
  Workflow,
  WorkflowNode,
  WorkflowEdge,
  ChatbotTheme,
  authApi,
} from '../api';
import { PipelineBuilderModal, KnowledgeBaseManageModal, PipelineSteps } from '../components/knowledge';
import type { PipelineStep } from '../components/knowledge';
import SortArrowsIcon from '../assets/icons/common/sort.svg?react';
import { adminApi } from '../api/aiProvider';
import '../styles/kbPipeline.css';
import userStepIllust01 from '../assets/illustrations/user-data-step-01.svg';
import userStepIllust02 from '../assets/illustrations/user-data-step-02.svg';
import userStepIllust03 from '../assets/illustrations/user-data-step-03.svg';
import userStepIllust04 from '../assets/illustrations/user-data-step-04.svg';
import userStepIllust05 from '../assets/illustrations/user-data-step-05.svg';
import adminStepIllust01 from '../assets/illustrations/admin-data-step-01.svg';
import adminStepIllust02 from '../assets/illustrations/admin-data-step-02.svg';
import adminStepIllust03 from '../assets/illustrations/admin-data-step-03.svg';
import adminStepIllust04 from '../assets/illustrations/admin-data-step-04.svg';
import adminStepIllust05 from '../assets/illustrations/admin-data-step-05.svg';
import userFileStepIllust01 from '../assets/illustrations/user-file-step-01.svg';
import userFileStepIllust02 from '../assets/illustrations/user-file-step-02.svg';
import adminFileStepIllust01 from '../assets/illustrations/admin-file-step-01.svg';
import adminFileStepIllust02 from '../assets/illustrations/admin-file-step-02.svg';
import userDbStepIllust01 from '../assets/illustrations/user-connector-step-01.svg';
import userDbStepIllust02 from '../assets/illustrations/user-connector-step-02.svg';
import adminDbStepIllust01 from '../assets/illustrations/admin-connector-step-01.svg';
import adminDbStepIllust02 from '../assets/illustrations/admin-connector-step-02.svg';
import userWebStepIllust01 from '../assets/illustrations/user-web-step-01.svg';
import userWebStepIllust02 from '../assets/illustrations/user-web-step-02.svg';
import adminWebStepIllust01 from '../assets/illustrations/admin-web-step-01.svg';
import adminWebStepIllust02 from '../assets/illustrations/admin-web-step-02.svg';
import emptySearchIllust from '../assets/illustrations/empty-search.svg';
import SettingsIcon from '../assets/icons/common/settings.svg?react';
import TrashIcon from '../assets/icons/common/trash.svg?react';
import SpinnerIcon from '../assets/icons/common/spinner.svg?react';

// 5단계 일러스트 - 역할(테마 색) × 하위 메뉴(소스 필터)  (index 0 = STEP 1)
// - default: 복합 데이터 소스 5단계 (기본)
// - file / db / web: 기본과 모양이 다른 단계만 앞에서부터 지정, 나머지 단계는 기본 사용
const STEP_ILLUSTS: Record<'user' | 'admin', Record<string, string[]>> = {
  user: {
    default: [userStepIllust01, userStepIllust02, userStepIllust03, userStepIllust04, userStepIllust05],
    file: [userFileStepIllust01, userFileStepIllust02],
    db: [userDbStepIllust01, userDbStepIllust02],
    web: [userWebStepIllust01, userWebStepIllust02],
  },
  admin: {
    default: [adminStepIllust01, adminStepIllust02, adminStepIllust03, adminStepIllust04, adminStepIllust05],
    file: [adminFileStepIllust01, adminFileStepIllust02],
    db: [adminDbStepIllust01, adminDbStepIllust02],
    web: [adminWebStepIllust01, adminWebStepIllust02],
  },
};

const getStepIllusts = (role: 'user' | 'admin', sourceFilter: string | null) => {
  const { default: base, ...bySource } = STEP_ILLUSTS[role];
  const override = (sourceFilter && bySource[sourceFilter]) || [];
  return base.map((src, i) => override[i] ?? src);
};

interface KnowledgeBasesPageProps {
  mode?: 'admin' | 'user';
}

// KB의 소스 타입들을 가져오는 유틸
function getKbSourceTypes(kb: KnowledgeBase): string[] {
  const config = kb.pipelineConfig as any;
  const sources = config?.sources;
  if (sources && sources.length > 0) {
    return sources.map((s: any) => s.type);
  }
  return [kb.sourceType || 'file'];
}

// 이 KB 가 목록에 보이는 페이지 주소 (아래 소스 필터와 같은 기준) + 관리 모달 열기(?manage=id)
// - 소스 2개 이상 = 복합 데이터 소스(/knowledge-bases), 1개 = 해당 소스 페이지(?source=file|db|web)
// - 관리자 대시보드 최근 지식 파이프라인 · 최근 활동에서 사용
const SOURCE_PARAM: Record<string, string> = { file: 'file', database: 'db', web: 'web' };
export function getKbManagePath(kb: KnowledgeBase): string {
  const sourceTypes = getKbSourceTypes(kb);
  const params = new URLSearchParams();
  if (sourceTypes.length === 1) params.set('source', SOURCE_PARAM[sourceTypes[0]] ?? sourceTypes[0]);
  params.set('manage', kb.id);
  return `/knowledge-bases?${params.toString()}`;
}

export function KnowledgeBasesPage({ mode = 'admin' }: KnowledgeBasesPageProps) {
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  // 사용자 모드일 때는 파일 업로드 필터 강제 적용
  const sourceFilter = mode === 'user' ? 'file' : searchParams.get('source'); // 'file', 'db', 'web', or null
  
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPipelineModalOpen, setIsPipelineModalOpen] = useState(false);
  const [pipelineModalKey, setPipelineModalKey] = useState(0);
  const [editingKb, setEditingKb] = useState<KnowledgeBase | null>(null);
  const [forcedSourceType, setForcedSourceType] = useState<'file' | 'web' | 'database' | undefined>(undefined);
  const [managingKb, setManagingKb] = useState<KnowledgeBase | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  
  // 챗봇 생성 모달
  const [createChatbotKb, setCreateChatbotKb] = useState<KnowledgeBase | null>(null);
  const [chatbotName, setChatbotName] = useState('');
  const [chatbotDesc, setChatbotDesc] = useState('');
  const [creatingChatbot, setCreatingChatbot] = useState(false);
  
  // 배포된 챗봇 목록
  const [deployedChatbots, setDeployedChatbots] = useState<Workflow[]>([]);
  
  // 챗봇 수정 모달
  const [editingChatbot, setEditingChatbot] = useState<Workflow | null>(null);
  const [editChatbotTab, setEditChatbotTab] = useState<'info' | 'theme' | 'ai'>('info');
  const [editTempName, setEditTempName] = useState('');
  const [editTempDesc, setEditTempDesc] = useState('');
  const [editTempTheme, setEditTempTheme] = useState<ChatbotTheme>({});
  const [editTempModel, setEditTempModel] = useState('claude-3-haiku');
  const [editTempProvider, setEditTempProvider] = useState<'bedrock' | 'anthropic'>('bedrock');
  const [anthropicConnected, setAnthropicConnected] = useState(false);
  const [editTempTemperature, setEditTempTemperature] = useState('0.7');
  const [editTempSystemPrompt, setEditTempSystemPrompt] = useState('');
  const [savingChatbot, setSavingChatbot] = useState(false);
  
  // 챗봇 수정 모달 드래그 이동
  const [editModalPos, setEditModalPos] = useState({ x: 0, y: 0 });
  const [isDraggingEditModal, setIsDraggingEditModal] = useState(false);
  const editModalDragOffset = useRef({ x: 0, y: 0 });
  const kbPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  // 목록 검색
  const [listSearchQuery, setListSearchQuery] = useState('');

  // 정렬
  const [sortKey, setSortKey] = useState<'name' | 'ownerName' | 'documentCount' | 'embeddingModel' | 'status' | 'createdAt' | 'lastSyncedAt'>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const handleSort = (key: typeof sortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
    setKbCurrentPage(1);
  };
  const SortIcon = ({ col }: { col: typeof sortKey }) => (
    <span className={`table-sort-icon${sortKey === col ? ` active ${sortDir}` : ''}`}>
      <SortArrowsIcon />
    </span>
  );
  
  // 검색 테스트
  const [selectedKbId, setSelectedKbId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [executedQuery, setExecutedQuery] = useState(''); // 실행된 검색어 (하이라이트용)
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [kbSelectError, setKbSelectError] = useState(false); // 지식베이스 미선택 에러

  // Text-to-SQL 테스트
  const [t2sSql, setT2sSql] = useState<string>('');
  const [t2sRows, setT2sRows] = useState<Record<string, unknown>[] | null>(null);
  const [t2sSummary, setT2sSummary] = useState<string>('');
  const [t2sRunning, setT2sRunning] = useState(false);
  const [t2sError, setT2sError] = useState<string>('');
  const [t2sSqlOpen, setT2sSqlOpen] = useState(false);

  const isUserMode = mode === 'user';
  // 로그인 역할 기준 (mode 는 포탈 화면 구분용이라 별개)
  const stepIllusts = getStepIllusts(isAdminRole(authApi.getUser()?.role) ? 'admin' : 'user', sourceFilter);

  // 페이징
  const KB_PAGE_SIZE = 5;
  const [kbCurrentPage, setKbCurrentPage] = useState(1);

  // 소스 필터에 따른 목록 필터링
  const filteredKnowledgeBases = knowledgeBases.filter((kb) => {
    // 텍스트 검색 필터
    if (listSearchQuery.trim()) {
      const query = listSearchQuery.toLowerCase();
      const name = (kb.name || '').toLowerCase();
      const ownerName = (kb.ownerName || kb.owner || '').toLowerCase();
      const embeddingModel = (kb.embeddingModel || '').toLowerCase();
      if (!name.includes(query) && !ownerName.includes(query) && !embeddingModel.includes(query)) {
        return false;
      }
    }
    
    // 소스 타입 필터
    const sourceTypes = getKbSourceTypes(kb);
    if (!sourceFilter) {
      // 복합 데이터 소스: 2개 이상의 소스 타입을 가진 KB만
      return sourceTypes.length > 1;
    } else {
      // 단일 소스 필터: 해당 소스 타입만 가지고 있는 KB
      const typeMap: Record<string, string> = { file: 'file', db: 'database', web: 'web' };
      const targetType = typeMap[sourceFilter] || sourceFilter;
      return sourceTypes.length === 1 && sourceTypes[0] === targetType;
    }
  }).sort((a, b) => {
    let av = '', bv = '';
    if (sortKey === 'name') { av = a.name || ''; bv = b.name || ''; }
    else if (sortKey === 'ownerName') { av = a.ownerName || a.owner || ''; bv = b.ownerName || b.owner || ''; }
    else if (sortKey === 'documentCount') { av = String(a.documentCount ?? 0).padStart(10, '0'); bv = String(b.documentCount ?? 0).padStart(10, '0'); }
    else if (sortKey === 'embeddingModel') { av = a.embeddingModel || ''; bv = b.embeddingModel || ''; }
    else if (sortKey === 'status') { av = a.status || ''; bv = b.status || ''; }
    else if (sortKey === 'createdAt') { av = a.createdAt || ''; bv = b.createdAt || ''; }
    else if (sortKey === 'lastSyncedAt') { av = a.lastSyncedAt || ''; bv = b.lastSyncedAt || ''; }
    return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  // 탭/검색 변경 시 1페이지로 초기화
  useEffect(() => { setKbCurrentPage(1); }, [sourceFilter, listSearchQuery]);

  // 페이징 계산
  const kbTotalPages = Math.ceil(filteredKnowledgeBases.length / KB_PAGE_SIZE);
  const pagedKnowledgeBases = filteredKnowledgeBases.slice(
    (kbCurrentPage - 1) * KB_PAGE_SIZE,
    kbCurrentPage * KB_PAGE_SIZE
  );

  useEffect(() => {
    loadKnowledgeBases();
    loadDeployedChatbots();
    // 언마운트 시 폴링 정리
    return () => { if (kbPollRef.current) { clearInterval(kbPollRef.current); kbPollRef.current = null; } };
    // Anthropic 연결 상태 로드
    adminApi.getProviderStatus('anthropic')
      .then((s) => setAnthropicConnected(s.connected))
      .catch(() => setAnthropicConnected(false));
  }, []);

  // ?manage=<id> 로 들어오면 (관리자 대시보드 최근 지식 파이프라인) 목록 로드 후 해당 관리 모달 열기
  // 연 뒤엔 파라미터를 지워서 모달을 닫고 새로고침해도 다시 열리지 않게
  const manageId = searchParams.get('manage');
  useEffect(() => {
    if (!manageId || knowledgeBases.length === 0) return;
    const target = knowledgeBases.find((kb) => kb.id === manageId);
    if (target) setManagingKb(target);
    const next = new URLSearchParams(searchParams);
    next.delete('manage');
    setSearchParams(next, { replace: true });
  }, [manageId, knowledgeBases]);

  const loadKnowledgeBases = async () => {
    try {
      setLoading(true);
      const data = await knowledgeBaseApi.list({ includeSystem: !isUserMode });
      // 스페이스 전용 KB는 파이프라인 UI에서 숨김 (space.routes.ts에서 자동 생성)
      setKnowledgeBases(data.filter(kb => !kb.id.startsWith('space-')));
      if (!selectedKbId) {
        // sourceFilter에 맞는 첫 번째 KB를 기본 선택
        const typeMap: Record<string, string> = { file: 'file', db: 'database', web: 'web' };
        const targetType = sourceFilter ? (typeMap[sourceFilter] || sourceFilter) : null;
        const filtered = targetType
          ? data.filter(kb => {
              const cfg = kb.pipelineConfig as any;
              const sources = cfg?.sources;
              const kbType = sources?.length > 0 ? sources[0].type : kb.sourceType;
              return kbType === targetType;
            })
          : data;
        if (filtered.length > 0) setSelectedKbId(filtered[0].id);
        else if (data.length > 0) setSelectedKbId(data[0].id);
      }

      // 처리 중인 KB가 있으면 자동 폴링 시작 (없으면 중지)
      const hasSyncing = data.some(kb => kb.status === 'syncing' || kb.status === 'indexing');
      if (hasSyncing) {
        if (!kbPollRef.current) {
          kbPollRef.current = setInterval(async () => {
            try {
              const fresh = await knowledgeBaseApi.list({ includeSystem: !isUserMode });
              setKnowledgeBases(fresh.filter(kb => !kb.id.startsWith('space-')));
              const stillSyncing = fresh.some(kb => kb.status === 'syncing' || kb.status === 'indexing');
              if (!stillSyncing) {
                clearInterval(kbPollRef.current!);
                kbPollRef.current = null;
              }
            } catch { /* ignore */ }
          }, 3000);
        }
      } else {
        if (kbPollRef.current) {
          clearInterval(kbPollRef.current);
          kbPollRef.current = null;
        }
      }
    } catch (error) {
      showToast('error', '지식베이스 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const loadDeployedChatbots = async () => {
    try {
      const workflows = await workflowApi.list();
      setDeployedChatbots(workflows.filter(w => w.status === 'deployed'));
    } catch (error) {
      console.error('배포된 챗봇 목록 로드 실패:', error);
    }
  };

  const findChatbotByKbId = (kbId: string): Workflow | undefined => {
    return deployedChatbots.find(w => {
      const retrieverNode = w.nodes.find(n => n.type === 'knowledge-retrieval');
      return retrieverNode?.data?.knowledgeBaseId === kbId;
    });
  };

  const handleOpenEditChatbot = async (chatbot: Workflow) => {
    try {
      const latestChatbot = await workflowApi.get(chatbot.id);
      const targetChatbot = latestChatbot || chatbot;
      setEditingChatbot(targetChatbot);
      setEditTempName(targetChatbot.name);
      setEditTempDesc(targetChatbot.description || '');
      setEditTempTheme(targetChatbot.theme || {});
      setEditChatbotTab('info');
      setEditModalPos({ x: 0, y: 0 }); // 모달 위치 초기화
      const llmNode = targetChatbot.nodes.find(n => n.type === 'llm');
      if (llmNode?.data) {
        setEditTempModel((llmNode.data.model as string) || 'nova-2-lite');
        setEditTempProvider((llmNode.data.provider as 'bedrock' | 'anthropic') || 'bedrock');
        setEditTempTemperature((llmNode.data.temperature as string) || '0.7');
        setEditTempSystemPrompt((llmNode.data.systemPrompt as string) || '');
      }
    } catch (error) {
      showToast('error', '챗봇 정보를 불러오는데 실패했습니다.');
    }
  };

  // 챗봇 수정 모달 드래그 핸들러
  const handleEditModalDragStart = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button, input, select, textarea')) return;
    setIsDraggingEditModal(true);
    editModalDragOffset.current = {
      x: e.clientX - editModalPos.x,
      y: e.clientY - editModalPos.y,
    };
    e.preventDefault();
  }, [editModalPos]);

  // 챗봇 수정 모달 드래그 이벤트
  useEffect(() => {
    if (!isDraggingEditModal) return;

    const handleMouseMove = (e: MouseEvent) => {
      setEditModalPos({
        x: e.clientX - editModalDragOffset.current.x,
        y: e.clientY - editModalDragOffset.current.y,
      });
    };

    const handleMouseUp = () => setIsDraggingEditModal(false);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingEditModal]);

  const handleSaveEditChatbot = async () => {
    if (!editingChatbot) return;
    try {
      setSavingChatbot(true);
      const cleanedTheme = { ...editTempTheme, quickPrompts: (editTempTheme.quickPrompts || []).filter(p => p.trim()) };
      const updatedNodes = editingChatbot.nodes.map((node) => {
        if (node.type === 'llm') {
          return { ...node, data: { ...node.data, provider: editTempProvider, model: editTempModel, temperature: editTempTemperature, systemPrompt: editTempSystemPrompt } };
        }
        return node;
      });
      await workflowApi.update(editingChatbot.id, { name: editTempName, description: editTempDesc, theme: cleanedTheme, nodes: updatedNodes });
      await loadDeployedChatbots();
      showToast('success', '챗봇이 수정되었습니다.');
      setEditingChatbot(null);
    } catch (error) {
      showToast('error', '챗봇 수정에 실패했습니다.');
    } finally {
      setSavingChatbot(false);
    }
  };

  const handleOpenPipeline = (kb?: KnowledgeBase) => {
    setEditingKb(kb || null);
    setPipelineModalKey(k => k + 1);
    setIsPipelineModalOpen(true);
  };

  const handlePipelineComplete = async () => {
    setIsPipelineModalOpen(false);
    setEditingKb(null);
    setForcedSourceType(undefined);
    await loadKnowledgeBases();
    showToast('success', '지식베이스가 생성되었습니다.');
  };

  const handleDelete = async (id: string) => {
    setDeleteConfirmId(id);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmId) return;
    const id = deleteConfirmId;
    setDeleteConfirmId(null);
    
    try {
      setDeletingId(id);
      await knowledgeBaseApi.delete(id);
      setKnowledgeBases((prev) => prev.filter((kb) => kb.id !== id));
      showToast('success', '지식베이스가 삭제되었습니다.');
    } catch (error: unknown) {
      const axiosErr = error as { response?: { status?: number; data?: { error?: { message?: string; inUseByWorkflows?: { name: string }[] } } } };
      if (axiosErr.response?.status === 409) {
        const workflows = axiosErr.response.data?.error?.inUseByWorkflows;
        const names = workflows?.map((w: { name: string }) => w.name).join(', ') || '';
        showToast('error', `사용 중인 워크플로우가 있어 삭제할 수 없습니다.\n(${names})`);
      } else {
        showToast('error', '지식베이스 삭제에 실패했습니다.');
      }
    } finally {
      setDeletingId(null);
    }
  };

  // 챗봇 생성 모달 열기
  const handleOpenCreateChatbot = (kb: KnowledgeBase) => {
    setCreateChatbotKb(kb);
    setChatbotName(`${kb.name} 챗봇`);
    setChatbotDesc(`${kb.name} 지식베이스 기반 RAG 챗봇`);
  };

  // 챗봇 생성 (RAG 워크플로우 자동 생성)
  const handleCreateChatbot = async () => {
    if (!createChatbotKb || !chatbotName.trim()) return;

    try {
      setCreatingChatbot(true);

      // 기본 RAG 워크플로우 노드 생성 (워크플로우 빌더와 동일한 타입 사용)
      const nodes: WorkflowNode[] = [
        {
          id: 'trigger-1',
          type: 'trigger',
          position: { x: 100, y: 200 },
          data: { label: '채팅 시작' },
        },
        {
          id: 'retriever-1',
          type: 'knowledge-retrieval',
          position: { x: 350, y: 200 },
          data: { 
            label: '문서 검색',
            knowledgeBaseId: createChatbotKb.id,
            topK: 5,
          },
        },
        {
          id: 'llm-1',
          type: 'llm',
          position: { x: 600, y: 200 },
          data: { 
            label: 'AI 응답',
            model: 'nova-2-lite',
            provider: 'bedrock',
            temperature: '0.7',
            systemPrompt: `당신은 도움이 되는 AI 어시스턴트입니다. 주어진 문서 내용을 바탕으로 사용자의 질문에 정확하게 답변해주세요.

문서에서 찾을 수 없는 내용에 대해서는 솔직히 "해당 정보를 찾을 수 없습니다"라고 답변하세요.`,
          },
        },
        {
          id: 'output-1',
          type: 'output',
          position: { x: 850, y: 200 },
          data: { label: '답변 전송' },
        },
      ];

      // 노드 연결
      const edges: WorkflowEdge[] = [
        { id: 'e1', source: 'trigger-1', target: 'retriever-1' },
        { id: 'e2', source: 'retriever-1', target: 'llm-1' },
        { id: 'e3', source: 'llm-1', target: 'output-1' },
      ];

      // 워크플로우 생성
      const workflow = await workflowApi.create({
        name: chatbotName,
        description: chatbotDesc,
        nodes,
        edges,
        knowledgeBaseIds: [createChatbotKb.id],
      });

      // 바로 배포
      await workflowApi.deploy(workflow.id);

      // 배포된 챗봇 목록 새로고침 (챗봇 만들기 → 수정 버튼 즉시 반영)
      await loadDeployedChatbots();

      showToast('success', `"${chatbotName}" 챗봇이 생성되고 배포되었습니다. 챗봇 메뉴에서 사용할 수 있습니다.`);
      setCreateChatbotKb(null);
      setChatbotName('');
      setChatbotDesc('');
    } catch (error) {
      showToast('error', '챗봇 생성에 실패했습니다.');
    } finally {
      setCreatingChatbot(false);
    }
  };

  const handleSearch = async () => {
    if (!selectedKbId) {
      setKbSelectError(true);
      showToast('info', '지식베이스를 선택해주세요.');
      return;
    }
    setKbSelectError(false);
    if (!searchQuery.trim()) return;
    
    try {
      setSearching(true);
      setSearchResults([]); // 이전 결과 초기화
      setExecutedQuery(searchQuery); // 실행된 검색어 저장 (하이라이트용)
      const result = await knowledgeBaseApi.search(selectedKbId, searchQuery);
      setSearchResults(result.results);
    } catch (error) {
      showToast('error', '검색에 실패했습니다.');
    } finally {
      setSearching(false);
    }
  };

  // 검색 결과 청크에서 질문 키워드를 하이라이트 (매칭된 부분을 <mark>로 강조)
  const highlightMatches = (text: string, query: string) => {
    const terms = query
      .split(/\s+/)
      .map((t) => t.trim())
      .filter((t) => t.length >= 2);
    if (terms.length === 0) return text;
    // 정규식 특수문자 이스케이프
    const escaped = terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const splitRe = new RegExp(`(${escaped.join('|')})`, 'gi');
    const matchRe = new RegExp(`^(${escaped.join('|')})$`, 'i');
    const parts = text.split(splitRe);
    return parts.map((part, i) =>
      matchRe.test(part) ? <mark key={i} className="kb-search-hl">{part}</mark> : <span key={i}>{part}</span>
    );
  };

  // text2sql KB 여부 판별 헬퍼
  const getT2sConnection = (kb: KnowledgeBase) => {
    const config = kb.pipelineConfig as any;
    const sources: any[] = config?.sources ?? [];
    const dbSrc = sources.find((s: any) => s.type === 'database') ?? config?.dbConfig;
    if (!dbSrc) return null;
    if (dbSrc.mode && dbSrc.mode !== 'text2sql') return null;
    if (!dbSrc.host) return null;
    return {
      type: (dbSrc.dbType ?? dbSrc.type ?? 'postgresql') as string,
      host: dbSrc.host as string,
      port: Number(dbSrc.port) || 5432,
      database: dbSrc.database as string,
      schema: dbSrc.schema as string | undefined,
      username: dbSrc.username as string,
      password: dbSrc.password as string | undefined,
    };
  };

  const selectedKb = knowledgeBases.find(kb => kb.id === selectedKbId);
  const t2sConnection = selectedKb ? getT2sConnection(selectedKb) : null;
  const isT2sMode = !!t2sConnection;

  const handleT2sRun = async () => {
    if (!searchQuery.trim() || !selectedKbId || !t2sConnection) return;
    setT2sRunning(true);
    setT2sSql('');
    setT2sRows(null);
    setT2sSummary('');
    setT2sError('');
    setT2sSqlOpen(false);

    try {
      const token = localStorage.getItem('agentbuilder_token');
      const res = await fetch(`/api/knowledge-bases/db/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          knowledgeBaseId: selectedKbId,
          query: searchQuery,
          connection: t2sConnection,
        }),
      });

      if (!res.ok || !res.body) {
        setT2sError(`요청 실패: HTTP ${res.status}`);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6)) as { type: string; data: unknown };
            if (event.type === 'sql') {
              setT2sSql(event.data as string);
              setT2sSqlOpen(true);
            } else if (event.type === 'rows') {
              setT2sRows(event.data as Record<string, unknown>[]);
            } else if (event.type === 'text') {
              setT2sSummary(prev => prev + (event.data as string));
            } else if (event.type === 'error') {
              setT2sError(event.data as string);
            }
          } catch { /* 파싱 오류 무시 */ }
        }
      }
    } catch (err) {
      setT2sError((err as Error).message);
    } finally {
      setT2sRunning(false);
    }
  };

  const getStatusBadge = (kb: KnowledgeBase) => {
    // 시안: 완료 = 회색, 진행 중 = 초록, 처리 필요/오류 = 빨강
    const colors: Record<string, BadgeColor> = {
      active: 'gray',
      syncing: 'success',
      indexing: 'success',
      error: 'error',
      'needs-sync': 'error',
    };

    const sourceType = getEffectiveSourceType(kb);
    
    const getLabel = (s: string) => {
      if (s === 'needs-sync') return '동기화 필요';
      // 수집량 0인데 active면 처리 실패로 표시
      if (s === 'active' && kb.documentCount === 0) {
        if (sourceType === 'web') return '크롤링 실패';
        if (sourceType === 'database') return '동기화 실패';
        return '처리 실패';
      }
      if (sourceType === 'web') {
        return { active: '크롤링 완료', syncing: '크롤링 중', indexing: '색인 중', error: '오류' }[s] || s;
      } else if (sourceType === 'database') {
        return { active: '동기화 완료', syncing: '동기화 중', indexing: '색인 중', error: '오류' }[s] || s;
      } else if (sourceType === 'mixed') {
        return { active: '처리 완료', syncing: '처리 중', indexing: '색인 중', error: '오류' }[s] || s;
      }
      return { active: '처리 완료', syncing: '처리 중', indexing: '색인 중', error: '오류' }[s] || s;
    };
    
    // 수집량 0이면서 active면 빨간색 배지
    const badgeColor = (kb.status === 'active' && kb.documentCount === 0) ? 'error' : (colors[kb.status] || 'gray');
    
    return <Badge color={badgeColor} dot>{getLabel(kb.status)}</Badge>;
  };

  // 소스 타입 레이블 (멀티 소스인 경우 "복합" 표시)
  const getEffectiveSourceType = (kb: KnowledgeBase): string => {
    const config = kb.pipelineConfig as any;
    const sources = config?.sources;
    if (sources && sources.length > 1) return 'mixed';
    return kb.sourceType || 'file';
  };

  const getSourceTypeLabel = (kb: KnowledgeBase) => {
    const type = getEffectiveSourceType(kb);
    const config = kb.pipelineConfig as any;
    const sources = config?.sources;
    
    if (type === 'mixed' && sources) {
      // 소스 타입들을 배지로 표시
      const typeSet = new Set<string>(sources.map((s: any) => s.type));
      const labels: Record<string, string> = { file: '파일', web: '웹', database: 'DB' };
      return (
        <div className="kb-source-tags">
          {Array.from(typeSet).map((t) => (
            <Badge key={t} variant={`source-${t}` as BadgeVariant} shape="tag">{labels[t] || t}</Badge>
          ))}
        </div>
      );
    }
    
    const labels: Record<string, string> = { file: '파일', web: '웹', database: 'DB', drive: 'Drive' };
    const t = type;
    return (
      <Badge variant={`source-${t}` as BadgeVariant} shape="tag">{labels[t] || t}</Badge>
    );
  };

  const getDocCountLabel = (kb: KnowledgeBase) => {
    const type = getEffectiveSourceType(kb);
    const count = kb.documentCount;
    if (type === 'mixed') return `${count}건`;
    if (type === 'web') return `${count} 페이지`;
    if (type === 'database') return `${count} 레코드`;
    return `${count} 문서`;
  };

  // 소스 필터에 따른 페이지 제목/설명
  const getPageTitle = () => {
    if (!sourceFilter) return '복합 데이터 소스';
    const titles: Record<string, string> = {
      file: '파일 업로드',
      db: 'DB 커넥터',
      web: '웹 크롤링',
    };
    return titles[sourceFilter] || '지식 파이프라인';
  };

  // 5단계 파이프라인 스텝 - 소스 필터별 문구
  const pipelineSteps: PipelineStep[] = [
    {
      illust: stepIllusts[0],
      title: !sourceFilter ? '수집' : sourceFilter === 'file' ? '파일 업로드' : sourceFilter === 'db' ? 'DB 연결' : '웹 크롤링',
      desc: !sourceFilter ? '파일 · 웹 · DB' : sourceFilter === 'file' ? 'PDF · DOCX · TXT · Excel' : sourceFilter === 'db' ? 'MySQL · PostgreSQL · Oracle' : 'URL · Sitemap · 블로그',
    },
    {
      illust: stepIllusts[1],
      title: !sourceFilter ? '추출 · 정제' : sourceFilter === 'file' ? '텍스트 추출' : sourceFilter === 'db' ? '쿼리 실행' : 'HTML 파싱',
      desc: !sourceFilter ? 'OCR, 표 구조화, 노이즈 제거' : sourceFilter === 'file' ? 'OCR(이미지 PDF 자동 처리), 표 구조화, 메타데이터' : sourceFilter === 'db' ? 'SELECT 실행, 컬럼 매핑' : '본문 추출, 네비게이션 제거',
    },
    {
      illust: stepIllusts[2],
      title: '청킹',
      desc: !sourceFilter ? '토큰 단위 분할 · 오버랩 적용' : sourceFilter === 'file' ? '문단/토큰 단위 분할' : sourceFilter === 'db' ? '레코드/필드 단위 분할' : '페이지/섹션 단위 분할',
    },
    { illust: stepIllusts[3], title: '색인', desc: '임베딩 · 벡터 저장소 적재' },
    { illust: stepIllusts[4], title: '검색 테스트', desc: '품질 검증 후 워크플로우 연결' },
  ];

  const getPageSubtitle = () => {
    if (!sourceFilter) return '파일·웹·DB를 조합하여 AI가 검색할 수 있는 지식베이스를 만듭니다';
    const subtitles: Record<string, string> = {
      file: 'PDF, DOCX, TXT 등의 문서 파일을 업로드하여 지식베이스를 구축합니다',
      db: '데이터베이스에 연결하여 실시간 데이터를 지식베이스로 활용합니다',
      web: '웹 페이지를 크롤링하여 최신 정보를 지식베이스에 반영합니다',
    };
    return subtitles[sourceFilter] || '';
  };

  return (
    <>
      {/* 관리자 모드에서만 Topbar 표시 */}
      {!isUserMode && (
        <Topbar
          title={getPageTitle()}
          description={getPageSubtitle()}
          actions={
            <Button color="primary" size="md" onClick={() => handleOpenPipeline()}>
              + 새 지식베이스
            </Button>
          }
        />
      )}

      <div className={isUserMode ? "portal-kb-content" : "page-content kb-page"}>
        <div className={isUserMode ? "" : "page-content-inner"}>
          {/* 사용자 모드 헤더 */}
          {isUserMode && (
            <div className="portal-kb-header">
              <div className="portal-kb-header-left">
                <h2 className="portal-kb-title">{getPageTitle()}</h2>
                <p className="portal-kb-subtitle">{getPageSubtitle()}</p>
              </div>
              <Button size='md' onClick={() => handleOpenPipeline()}>
                + 새 지식베이스
              </Button>
            </div>
          )}

          {/* 파이프라인 단계 소개 */}
          <div className="intro">
            {!sourceFilter ? (
              <>파일(PDF·DOCX·TXT), 웹 크롤링, DB 연결을 <b>하나의 지식베이스에 함께</b> 구성할 수 있습니다. 수집된 데이터는 <b>추출·정제 → 청킹 → 색인</b>을 거쳐 워크플로우의 "문서 검색" 노드가 사용합니다.</>
            ) : sourceFilter === 'file' ? (
              <>PDF, DOCX, TXT 등의 문서 파일을 업로드하면 <b>텍스트 추출 → 청킹 → 임베딩</b> 과정을 거쳐 검색 가능한 지식베이스가 됩니다. 스캔·이미지 형태의 PDF는 <b>OCR(Amazon Textract)</b>로 자동 텍스트를 추출합니다.</>
            ) : sourceFilter === 'db' ? (
              <>데이터베이스에 연결하여 <b>쿼리 실행 → 데이터 정제 → 청킹 → 임베딩</b> 과정을 거쳐 실시간 데이터를 지식베이스로 활용합니다.</>
            ) : sourceFilter === 'web' ? (
              <>웹 페이지를 크롤링하여 <b>HTML 파싱 → 텍스트 추출 → 청킹 → 임베딩</b> 과정을 거쳐 최신 정보를 지식베이스에 반영합니다.</>
            ) : null}
          </div>

          {/* 5단계 파이프라인 스텝 카드 - 소스 타입별 */}
          <div className='pipeline-steps-wrap'>
            <PipelineSteps steps={pipelineSteps} />
          </div>

          {/* 지식베이스 목록 */}
          <div className="section-label">
            <span>지식베이스</span>
          </div>
          
          {knowledgeBases.length > 0 && (
            <div className="kb-list-search">
              <SearchInput
                placeholder="이름, 소유자, 임베딩 모델로 검색..."
                value={listSearchQuery}
                onChange={(e) => setListSearchQuery(e.target.value)}
                onClear={() => setListSearchQuery('')}
              />
              <button
                className="kb-search-refresh"
                onClick={loadKnowledgeBases}
                disabled={loading}
                title="새로고침"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/>
                  <path d="M21 3v5h-5"/>
                </svg>
              </button>
            </div>
          )}
          
          {loading ? (
            <div className="loading">로딩 중...</div>
          ) : (
            <div className="table-wrap">
              <table className="kb-list-table">
                {/* 열 너비 고정 (kbPipeline.css .kb-list-table) - 내용 따라 열이 움직이지 않게, 남는 폭은 열 비율대로 */}
                <colgroup>
                  <col className="kb-col-name" />
                  <col className="kb-col-source" />
                  {!isUserMode && <col className="kb-col-owner" />}
                  <col className="kb-col-count" />
                  <col className="kb-col-model" />
                  <col className="kb-col-status" />
                  <col className="kb-col-date" />
                  <col className="kb-col-synced" />
                  <col className="kb-col-actions" />
                </colgroup>
                <thead>
                  <tr>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>이름 <SortIcon col="name" /></th>
                    <th>소스</th>
                    {!isUserMode && <th style={{ cursor: 'pointer' }} onClick={() => handleSort('ownerName')}>소유자 <SortIcon col="ownerName" /></th>}
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('documentCount')}>수집량 <SortIcon col="documentCount" /></th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('embeddingModel')}>임베딩 모델 <SortIcon col="embeddingModel" /></th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('status')}>상태 <SortIcon col="status" /></th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('createdAt')}>등록일 <SortIcon col="createdAt" /></th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('lastSyncedAt')}>마지막 동기화 <SortIcon col="lastSyncedAt" /></th>
                    <th>작업</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredKnowledgeBases.length === 0 ? (
                    <tr>
                      <td colSpan={isUserMode ? 8 : 9} className="table-empty">
                        {knowledgeBases.length === 0 
                          ? '아직 지식베이스가 없습니다. "+ 새 지식베이스" 버튼을 눌러 시작하세요.'
                          : '조건에 맞는 지식베이스가 없습니다.'}
                      </td>
                    </tr>
                  ) : (
                    pagedKnowledgeBases.map((kb) => (
                    <tr key={kb.id}>
                      <td className="kb-name left" title={kb.name}>
                        {/* 이름 클릭 = 관리(⚙) 버튼과 같은 관리 모달 */}
                        <a href="#" className="table-link" onClick={(e) => { e.preventDefault(); setManagingKb(kb); }}>
                          {kb.name}
                        </a>
                      </td>
                      <td>{getSourceTypeLabel(kb)}</td>
                      {!isUserMode && <td>{kb.ownerName || '시스템'}</td>}
                      <td>{getDocCountLabel(kb)}</td>
                      <td>
                        {kb.embeddingModel?.split(':')[0]?.replace('amazon.', '') || 'Titan V2'}
                      </td>
                      <td>{getStatusBadge(kb)}</td>
                      <td>
                        {kb.createdAt 
                          ? new Date(kb.createdAt).toLocaleDateString() 
                          : '-'}
                      </td>
                      <td>
                        {kb.lastSyncedAt 
                          ? new Date(kb.lastSyncedAt).toLocaleString() 
                          : '-'}
                      </td>
                      <td>
                        <div className="kb-action-btn-row">
                          <button
                            className="kb-action-btn"
                            onClick={() => setManagingKb(kb)}
                            disabled={deletingId === kb.id}
                            title="관리"
                          >
                            <SettingsIcon />
                          </button>
                          {/* 챗봇 만들기/수정 버튼 - 사용자 모드에서만 표시 */}
                          {isUserMode && kb.status === 'active' && (() => {
                            const existingChatbot = findChatbotByKbId(kb.id);
                            return existingChatbot ? (
                              <button 
                                className="kb-action-btn kb-action-btn-edit"
                                onClick={() => handleOpenEditChatbot(existingChatbot)}
                                disabled={deletingId === kb.id}
                                title="챗봇 수정"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                </svg>
                              </button>
                            ) : (
                              <button 
                                className="kb-action-btn kb-action-btn-chatbot"
                                onClick={() => handleOpenCreateChatbot(kb)}
                                disabled={deletingId === kb.id}
                                title="챗봇 만들기"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                                </svg>
                              </button>
                            );
                          })()}
                          <button 
                            className="kb-action-btn kb-action-btn-danger"
                            onClick={() => handleDelete(kb.id)}
                            disabled={deletingId !== null}
                            title="삭제"
                          >
                            {deletingId === kb.id ? (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spin">
                                <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/>
                                <path d="M21 3v5h-5"/>
                              </svg>
                            ) : (
                              <TrashIcon />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* 페이지네이션 */}
          {kbTotalPages > 1 && (
            <Pagination
              current={kbCurrentPage}
              total={kbTotalPages}
              onChange={setKbCurrentPage}
              info={<><b>{(kbCurrentPage - 1) * KB_PAGE_SIZE + 1}–{Math.min(kbCurrentPage * KB_PAGE_SIZE, filteredKnowledgeBases.length)}</b> / {filteredKnowledgeBases.length}개</>}
            />
          )}

          {/* 검색 테스트 섹션 */}
          <div className="section-label">
            <span>검색 테스트</span>
            <span className="section-label-desc">질문을 입력하면 챗봇이 답변할 때 참고할 문서 내용을 미리 확인할 수 있어요. 질문과 가장 관련 있는 순서로 보여지며, 겹치는 단어는 하이라이트됩니다.</span>
          </div>

          <div className="kb-search-layout">
            <div className="kb-search-left">
              <div className="pipe-settings-field" style={{ maxWidth: 'none' }}>
                <label className="pipe-settings-label">지식베이스</label>
                <Select
                  value={selectedKbId}
                  onChange={(e) => {
                    setSelectedKbId(e.target.value);
                    setKbSelectError(false);
                    setSearchQuery('');
                    setExecutedQuery('');
                    setSearchResults([]);
                    setT2sSql(''); setT2sRows(null); setT2sSummary(''); setT2sError('');
                  }}
                  options={[
                    { value: '', label: '::: 지식베이스 선택 :::' },
                    ...filteredKnowledgeBases.map((kb) => ({ value: kb.id, label: kb.name }))
                  ]}
                  className={kbSelectError ? 'select-error' : ''}
                />
                {isT2sMode && (
                  <div className='pipe-settings-chips'>
                    <span className='pipe-settings-chip'>Text-to-SQL 모드</span>
                    <span className='pipe-settings-chip'>Amazon Bedrock · Nova Lite</span>
                  </div>
                )}
              </div>
              <div className="pipe-settings-field" style={{ maxWidth: 'none' }}>
                <label className="pipe-settings-label">질문</label>
                {/* 돋보기 클릭 · Enter 로 실행 (실행 중엔 돋보기 → 스피너) - 빈 질문은 핸들러에서 무시 */}
                <div className="pipe-search-row">
                  <SearchInput
                    placeholder="질문을 입력하세요."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onSearch={isT2sMode ? handleT2sRun : handleSearch}
                    loading={isT2sMode ? t2sRunning : searching}
                  />
                </div>
              </div>
            </div>
            
            <div className="kb-search-right">
              {isT2sMode ? (
                /* ── Text-to-SQL 결과 ── */
                t2sError ? (
                  <div className="pipe-result-card" style={{ borderColor: 'var(--red-soft)' }}>
                    <div style={{ color: 'var(--red)', fontSize: '12px' }}>⚠️ {t2sError}</div>
                  </div>
                ) : t2sSummary || t2sSql ? (
                  <>
                    {/* 생성된 SQL - 접힘 섹션 */}
                    {t2sSql && (
                      <div className="pipe-result-card" style={{ marginBottom: '8px' }}>
                        <button
                          style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '12px', fontWeight: 600, color: 'var(--text-2)' }}
                          onClick={() => setT2sSqlOpen(o => !o)}
                        >
                          <span>{t2sSqlOpen ? '▼' : '▶'}</span>
                          <span>🔍 생성된 SQL</span>
                        </button>
                        {t2sSqlOpen && (
                          <pre style={{ marginTop: '8px', fontSize: '11.5px', background: 'var(--bg-3)', borderRadius: '6px', padding: '10px', overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: 'var(--text-1)' }}>
                            {t2sSql}
                          </pre>
                        )}
                      </div>
                    )}
                    {/* 조회 결과 요약 */}
                    {t2sSummary && (
                      <div className="pipe-result-card">
                        <div className="pipe-result-head">
                          <span>조회 결과</span>
                          {t2sRows && <span className="pipe-result-score">{t2sRows.length}행</span>}
                        </div>
                        <div className="pipe-result-text" style={{ whiteSpace: 'pre-wrap' }}>
                          {t2sSummary}
                        </div>
                      </div>
                    )}
                    {/* 스트리밍 중 로딩 표시 */}
                    {t2sRunning && !t2sSummary && (
                      <div className="pipe-result-card">
                        <div style={{ fontSize: '12px', color: 'var(--text-3)' }}>결과 요약 생성 중...</div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="empty-state">
                    {t2sRunning ? <SpinnerIcon className='search-input-spinner' /> : <img src={emptySearchIllust} alt="" />}
                    {t2sRunning ? 'SQL 생성 중...' : '자연어 질문을 입력하고 Enter 또는 돋보기를 누르세요'}
                  </div>
                )
              ) : (
                /* ── 벡터 검색 결과 ── */
                searchResults.length === 0 ? (
                  <div className="empty-state">
                    {searching ? <SpinnerIcon className='search-input-spinner' /> : <img src={emptySearchIllust} alt="" />}
                    {searching ? '관련 문서를 찾는 중...' : '검색 결과가 없습니다'}
                  </div>
                ) : (
                  searchResults.map((result, idx) => (
                    <div key={idx} className="pipe-result-card">
                      <div className="pipe-result-head">
                        <span>{result.documentName}</span>
                        <span className="pipe-result-score">
                          {(result.score * 100).toFixed(1)}%
                        </span>
                      </div>
                      <div className="pipe-result-text">
                        {highlightMatches(result.content.substring(0, 300), executedQuery)}
                        {result.content.length > 300 && '...'}
                      </div>
                    </div>
                  ))
                )
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 파이프라인 빌더 모달 */}
      {isPipelineModalOpen && (
        <PipelineBuilderModal
          key={pipelineModalKey}
          knowledgeBase={editingKb}
          mode={mode}
          defaultSourceType={
            forcedSourceType ??
            (sourceFilter === 'db' ? 'database'
            : sourceFilter === 'web' ? 'web'
            : sourceFilter === 'file' ? 'file'
            : undefined)
          }
          onClose={() => {
            setIsPipelineModalOpen(false);
            setEditingKb(null);
            setForcedSourceType(undefined);
          }}
          onComplete={handlePipelineComplete}
        />
      )}

      {/* KB 관리 모달 */}
      {managingKb && (
        <KnowledgeBaseManageModal
          knowledgeBase={managingKb}
          onClose={() => setManagingKb(null)}
          onUpdate={loadKnowledgeBases}
          onEditDbConfig={(kb) => {
            setManagingKb(null);
            setEditingKb(kb);
            setForcedSourceType('database');
            setPipelineModalKey(k => k + 1);
            setIsPipelineModalOpen(true);
          }}
        />
      )}

      {/* 챗봇 생성 모달 */}
      {createChatbotKb && (
        <div
          className="modal-overlay"
          // 바깥 클릭 = 닫기 (공통 Modal 과 동일) - 누른 곳 · 뗀 곳 모두 바깥일 때만 (입력 중 드래그로 닫힘 방지)
          onMouseDown={(e) => { e.currentTarget.dataset.downOutside = e.target === e.currentTarget ? '1' : ''; }}
          onClick={(e) => { if (e.target === e.currentTarget && e.currentTarget.dataset.downOutside) setCreateChatbotKb(null); }}
        >
          <div className="modal" style={{ width: '400px' }}>
            <div className="modal-header">
              <h3>🤖 챗봇 만들기</h3>
              <button className="modal-close" onClick={() => setCreateChatbotKb(null)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '13px', color: 'var(--text-2)', marginBottom: '16px' }}>
                <b>"{createChatbotKb.name}"</b> 지식베이스를 기반으로 RAG 챗봇을 자동 생성합니다.
              </p>
              <div className="form-field">
                <label>챗봇 이름 *</label>
                <input
                  type="text"
                  value={chatbotName}
                  onChange={(e) => setChatbotName(e.target.value)}
                  placeholder="예: 제품 Q&A 챗봇"
                />
              </div>
              <div className="form-field">
                <label>설명 (선택)</label>
                <input
                  type="text"
                  value={chatbotDesc}
                  onChange={(e) => setChatbotDesc(e.target.value)}
                  placeholder="예: 제품 매뉴얼 기반 질의응답"
                />
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-3)', marginTop: '8px', padding: '10px', background: 'var(--bg-2)', borderRadius: '8px' }}>
                <div style={{ marginBottom: '4px' }}>📋 자동 생성되는 워크플로우:</div>
                <div>입력 → 문서검색 → LLM(Claude 3.5 Haiku) → 출력</div>
              </div>
            </div>
            <div className="modal-footer">
              <Button variant="secondary" onClick={() => setCreateChatbotKb(null)}>
                취소
              </Button>
              <Button 
                onClick={handleCreateChatbot} 
                disabled={!chatbotName.trim() || creatingChatbot}
              >
                {creatingChatbot ? '생성 중...' : '챗봇 생성'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 챗봇 수정 모달 */}
      {editingChatbot && (
        <div
          className="modal-overlay"
          // 바깥 클릭 = 닫기 (공통 Modal 과 동일) - 누른 곳 · 뗀 곳 모두 바깥일 때만 (헤더 드래그 이동 후 닫힘 방지)
          onMouseDown={(e) => { e.currentTarget.dataset.downOutside = e.target === e.currentTarget ? '1' : ''; }}
          onClick={(e) => { if (e.target === e.currentTarget && e.currentTarget.dataset.downOutside) setEditingChatbot(null); }}
        >
          <div
            className={`modal chatbot-create-modal${isDraggingEditModal ? ' dragging' : ''}`} 
            style={{ 
              width: '1000px', 
              maxWidth: '90vw', 
              maxHeight: '85vh',
              transform: `translate(${editModalPos.x}px, ${editModalPos.y}px)`,
            }}
          >
            <div 
              className="modal-header" 
              style={{ cursor: 'move' }}
              onMouseDown={handleEditModalDragStart}
            >
              <h3>✏️ 챗봇 수정</h3>
              <button className="modal-close" onClick={() => setEditingChatbot(null)}>×</button>
            </div>
            <div className="chatbot-create-tabs">
              <button className={`chatbot-create-tab ${editChatbotTab === 'info' ? 'active' : ''}`} onClick={() => setEditChatbotTab('info')}>📋 기본 정보</button>
              <button className={`chatbot-create-tab ${editChatbotTab === 'theme' ? 'active' : ''}`} onClick={() => setEditChatbotTab('theme')}>🎨 테마</button>
              <button className={`chatbot-create-tab ${editChatbotTab === 'ai' ? 'active' : ''}`} onClick={() => setEditChatbotTab('ai')}>🤖 AI 설정</button>
            </div>
            <div className="modal-body">
              {editChatbotTab === 'info' && (
                <div className="chatbot-create-info">
                  <div className="form-field"><label>챗봇 이름 *</label><input type="text" value={editTempName} onChange={(e) => setEditTempName(e.target.value)} placeholder="예: 제품 Q&A 챗봇" /></div>
                  <div className="form-field"><label>설명 (선택)</label><textarea rows={3} value={editTempDesc} onChange={(e) => setEditTempDesc(e.target.value)} placeholder="예: 제품 매뉴얼 기반 질의응답 챗봇입니다." style={{ resize: 'vertical' }} /></div>
                </div>
              )}
              {editChatbotTab === 'theme' && (
                <div className="chatbot-create-theme">
                  <div className="theme-modal-body">
                    <div className="theme-settings-panel">
                      <div className="form-field"><label>챗봇 표시 이름</label><input type="text" placeholder="예: 출장 도우미" value={editTempTheme.botName || ''} onChange={(e) => setEditTempTheme({ ...editTempTheme, botName: e.target.value })} /></div>
                      <div className="theme-row">
                        <div className="form-field"><label>메인 색상</label><div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}><input type="color" value={editTempTheme.primaryColor || '#4ade80'} onChange={(e) => setEditTempTheme({ ...editTempTheme, primaryColor: e.target.value })} style={{ width: '40px', height: '32px', padding: '2px', cursor: 'pointer' }} /><input type="text" value={editTempTheme.primaryColor || ''} onChange={(e) => setEditTempTheme({ ...editTempTheme, primaryColor: e.target.value })} style={{ flex: 1 }} /></div></div>
                        <div className="form-field"><label>배경 색상</label><div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}><input type="color" value={editTempTheme.backgroundColor || '#f8fafc'} onChange={(e) => setEditTempTheme({ ...editTempTheme, backgroundColor: e.target.value })} style={{ width: '40px', height: '32px', padding: '2px', cursor: 'pointer' }} /><input type="text" value={editTempTheme.backgroundColor || ''} onChange={(e) => setEditTempTheme({ ...editTempTheme, backgroundColor: e.target.value })} style={{ flex: 1 }} /></div></div>
                      </div>
                      <div className="form-field"><label>챗봇 아이콘</label><div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}><label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}><input type="radio" name="editBotIcon" checked={editTempTheme.botIconStyle !== 'black'} onChange={() => setEditTempTheme({ ...editTempTheme, botIconStyle: 'white' })} /><span style={{ width: '28px', height: '28px', borderRadius: '50%', background: editTempTheme.primaryColor || '#4ade80', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><img src="/icons8-chatbot-32.png" alt="white" style={{ width: '24px', height: '24px' }} /></span><span>흰색</span></label><label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}><input type="radio" name="editBotIcon" checked={editTempTheme.botIconStyle === 'black'} onChange={() => setEditTempTheme({ ...editTempTheme, botIconStyle: 'black' })} /><span style={{ width: '28px', height: '28px', borderRadius: '50%', background: editTempTheme.primaryColor || '#4ade80', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><img src="/icons8-chatbot-32_white.png" alt="black" style={{ width: '24px', height: '24px' }} /></span><span>검정</span></label></div></div>
                      <div className="form-field"><label>환영 메시지</label><textarea rows={2} placeholder="안녕하세요! 무엇이든 물어보세요." value={editTempTheme.welcomeMessage || ''} onChange={(e) => setEditTempTheme({ ...editTempTheme, welcomeMessage: e.target.value })} /></div>
                      <div className="theme-section-title">사용자 질문 스타일</div>
                      <div className="theme-row">
                        <div className="form-field"><label>질문 배경색</label><div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}><input type="color" value={editTempTheme.userBubbleColor || '#dcfce7'} onChange={(e) => setEditTempTheme({ ...editTempTheme, userBubbleColor: e.target.value })} style={{ width: '40px', height: '32px', padding: '2px', cursor: 'pointer' }} /><input type="text" value={editTempTheme.userBubbleColor || ''} onChange={(e) => setEditTempTheme({ ...editTempTheme, userBubbleColor: e.target.value })} style={{ flex: 1 }} /></div></div>
                        <div className="form-field"><label>질문 글자색</label><div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}><input type="color" value={editTempTheme.userTextColor || '#1e1b4b'} onChange={(e) => setEditTempTheme({ ...editTempTheme, userTextColor: e.target.value })} style={{ width: '40px', height: '32px', padding: '2px', cursor: 'pointer' }} /><input type="text" value={editTempTheme.userTextColor || ''} onChange={(e) => setEditTempTheme({ ...editTempTheme, userTextColor: e.target.value })} style={{ flex: 1 }} /></div></div>
                      </div>
                      <div className="theme-section-title">챗봇 답변 스타일</div>
                      <div className="theme-row">
                        <div className="form-field"><label>답변 배경색</label><div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}><input type="color" value={editTempTheme.botBubbleColor || '#ffffff'} onChange={(e) => setEditTempTheme({ ...editTempTheme, botBubbleColor: e.target.value })} style={{ width: '40px', height: '32px', padding: '2px', cursor: 'pointer' }} /><input type="text" value={editTempTheme.botBubbleColor || ''} onChange={(e) => setEditTempTheme({ ...editTempTheme, botBubbleColor: e.target.value })} style={{ flex: 1 }} /></div></div>
                        <div className="form-field"><label>답변 글자색</label><div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}><input type="color" value={editTempTheme.botTextColor || '#374151'} onChange={(e) => setEditTempTheme({ ...editTempTheme, botTextColor: e.target.value })} style={{ width: '40px', height: '32px', padding: '2px', cursor: 'pointer' }} /><input type="text" value={editTempTheme.botTextColor || ''} onChange={(e) => setEditTempTheme({ ...editTempTheme, botTextColor: e.target.value })} style={{ flex: 1 }} /></div></div>
                      </div>
                    </div>
                    <div className="theme-preview-panel">
                      <div className="theme-preview-label">미리보기</div>
                      <div className="theme-preview-container" style={{ background: editTempTheme.backgroundColor || '#f8fafc' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderRadius: '8px 8px 0 0', marginBottom: '12px', background: editTempTheme.primaryColor || '#4ade80', color: '#fff' }}><span style={{ fontWeight: 600, fontSize: '13px' }}>{editTempTheme.botName || editTempName}</span><span style={{ fontSize: '11px', opacity: 0.8 }}>대화 초기화</span></div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px', alignItems: 'flex-start', gap: '8px' }}><div style={{ background: editTempTheme.userBubbleColor || '#dcfce7', color: editTempTheme.userTextColor || '#1e1b4b', padding: '8px 12px', borderRadius: '12px', borderTopRightRadius: '2px', fontSize: '13px', maxWidth: '70%' }}>출장 규정이 어떻게 되나요?</div><div style={{ width: '28px', height: '28px', borderRadius: '50%', background: editTempTheme.userBubbleColor || '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', flexShrink: 0 }}>👤</div></div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '12px' }}><div style={{ width: '28px', height: '28px', borderRadius: '50%', background: editTempTheme.primaryColor || '#4ade80', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><img src={editTempTheme.botIconStyle === 'black' ? '/icons8-chatbot-32_white.png' : '/icons8-chatbot-32.png'} alt="bot" style={{ width: '24px', height: '24px' }} /></div><div style={{ background: editTempTheme.botBubbleColor || '#ffffff', color: editTempTheme.botTextColor || '#374151', padding: '8px 12px', borderRadius: '12px', borderTopLeftRadius: '2px', fontSize: '13px', border: '1px solid #e5e7eb', maxWidth: '70%' }}>{editTempTheme.welcomeMessage || '안녕하세요! 무엇이든 물어보세요.'}</div></div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>{((editTempTheme.quickPrompts || []).filter(p => p.trim()).length > 0 ? editTempTheme.quickPrompts!.filter(p => p.trim()).slice(0, 3) : ['질문 예시 1', '질문 예시 2']).map((prompt, i) => (<span key={i} style={{ padding: '6px 12px', borderRadius: '16px', border: `1px solid ${editTempTheme.primaryColor || '#4ade80'}`, color: editTempTheme.primaryColor || '#4ade80', fontSize: '12px', background: '#fff' }}>{prompt.length > 20 ? prompt.slice(0, 20) + '...' : prompt}</span>))}</div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', background: '#fff', borderRadius: '8px', padding: '8px 10px', border: '1px solid #e5e7eb' }}><div style={{ flex: 1, fontSize: '13px', color: '#9ca3af' }}>메시지를 입력하세요...</div><div style={{ width: '32px', height: '32px', borderRadius: '8px', background: editTempTheme.primaryColor || '#4ade80', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '12px' }}>▶</div></div>
                      </div>
                      <div className="form-field" style={{ marginTop: '16px' }}><label>빠른 질문 버튼</label><div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>{(editTempTheme.quickPrompts || ['']).map((prompt, index) => (<div key={index} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}><input type="text" placeholder={`질문 ${index + 1}`} value={prompt} onChange={(e) => { const newPrompts = [...(editTempTheme.quickPrompts || [''])]; newPrompts[index] = e.target.value; setEditTempTheme({ ...editTempTheme, quickPrompts: newPrompts }); }} style={{ flex: 1 }} /><button type="button" onClick={() => { const newPrompts = (editTempTheme.quickPrompts || ['']).filter((_, i) => i !== index); setEditTempTheme({ ...editTempTheme, quickPrompts: newPrompts.length ? newPrompts : [''] }); }} style={{ padding: '5px 8px', background: 'none', border: 'var(--border)', borderRadius: '4px', cursor: 'pointer', color: 'var(--text-3)', fontSize: '12px' }} title="삭제">✕</button></div>))}<button type="button" onClick={() => setEditTempTheme({ ...editTempTheme, quickPrompts: [...(editTempTheme.quickPrompts || ['']), ''] })} style={{ padding: '6px 10px', background: 'none', border: '1px dashed var(--border-color)', borderRadius: '4px', cursor: 'pointer', color: 'var(--text-2)', fontSize: '12px' }}>+ 질문 추가</button></div></div>
                    </div>
                  </div>
                </div>
              )}
              {editChatbotTab === 'ai' && (
                <div className="chatbot-create-ai">
                  <div className="theme-row">
                    <div className="form-field">
                      <label>AI 제공자</label>
                      <select value={editTempProvider} onChange={(e) => {
                        const prov = e.target.value as 'bedrock' | 'anthropic';
                        setEditTempProvider(prov);
                        setEditTempModel(prov === 'anthropic' ? 'claude-haiku-4-5' : 'nova-2-lite');
                      }}>
                        <option value="bedrock">Amazon Bedrock</option>
                        <option value="anthropic" disabled={!anthropicConnected}>Anthropic{!anthropicConnected ? ' (미연결)' : ''}</option>
                      </select>
                    </div>
                    <div className="form-field">
                      <label>모델 선택</label>
                      <select value={editTempModel} onChange={(e) => setEditTempModel(e.target.value)}>
                        {editTempProvider === 'anthropic' ? (
                          <>
                            <option value="claude-haiku-4-5">Claude Haiku 4.5 (빠른 응답)</option>
                            <option value="claude-sonnet-4">Claude Sonnet 4</option>
                            <option value="claude-sonnet-4-5">Claude Sonnet 4.5</option>
                            <option value="claude-opus-4-5">Claude Opus 4.5 (최고 성능)</option>
                          </>
                        ) : (
                          <>
                            <option value="nova-2-lite">Amazon Nova 2 Lite (빠른 응답)</option>
                            <option value="nova-2-pro">Amazon Nova 2 Pro</option>
                            <option value="claude-haiku-4-5">Claude Haiku 4.5 via Bedrock</option>
                            <option value="claude-sonnet-4">Claude Sonnet 4 via Bedrock</option>
                          </>
                        )}
                      </select>
                    </div>
                    <div className="form-field"><label>Temperature (창의성)</label><select value={editTempTemperature} onChange={(e) => setEditTempTemperature(e.target.value)}><option value="0">0 - 가장 일관된 응답</option><option value="0.3">0.3 - 일관된 응답</option><option value="0.5">0.5 - 균형 잡힌 응답</option><option value="0.7">0.7 - 창의적 응답 (기본)</option><option value="1.0">1.0 - 매우 창의적</option></select></div>
                  </div>
                  <div className="form-field"><label>시스템 프롬프트</label><p style={{ fontSize: '12px', color: 'var(--text-3)', marginBottom: '6px' }}>AI가 어떻게 응답해야 하는지 지시사항을 입력하세요.</p><textarea rows={6} placeholder="예: 당신은 친절한 고객 서비스 담당자입니다." value={editTempSystemPrompt} onChange={(e) => setEditTempSystemPrompt(e.target.value)} /></div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <Button variant="secondary" onClick={() => setEditingChatbot(null)}>취소</Button>
              <Button onClick={handleSaveEditChatbot} disabled={!editTempName.trim() || savingChatbot}>{savingChatbot ? '저장 중...' : '저장'}</Button>
            </div>
          </div>
        </div>
      )}

      {/* 삭제 확인 모달 */}
      <Modal
        isOpen={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
        title="지식베이스 삭제"
        size="sm"
        style={{ maxWidth: 360 }}
        bodyClassName="chat-confirm-body"
        footerLeft={
          <Button color="gray" appearance="outline" onClick={() => setDeleteConfirmId(null)}>취소</Button>
        }
        footerRight={
          <Button color="error" onClick={confirmDelete}>삭제</Button>
        }
      >
        지식베이스를 삭제하시겠습니까?<br />포함된 모든 문서도 함께 삭제되며, 복구할 수 없습니다.
      </Modal>
    </>
  );
}

export default KnowledgeBasesPage;
